import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { StarPicker } from '../components/StarRating';
import { StarVoting, ThumbsVoting, MANAGER_CATEGORIES, TENANT_CATEGORIES } from '../components/ReviewCategories';
import { DisclaimerModal } from '../components/DisclaimerModal';

export default function WriteReview({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingReviewId = searchParams.get('edit');
  const isEditing = !!editingReviewId;
  const [profile, setProfile] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [votes, setVotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ratingError, setRatingError] = useState(false);
  const [missing, setMissing] = useState(new Set());
  const [aiChecking, setAiChecking] = useState(false);
  const [aiViolations, setAiViolations] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    if (submitted) window.scrollTo(0, 0);
  }, [submitted]);

  useEffect(() => {
    async function loadProfile() {
      const { data: managerData } = await supabase.from('managers').select('*').eq('id', id).maybeSingle();
      if (managerData) {
        setProfile({ ...managerData, type: 'manager' });
        return;
      }
      const { data: tenantData } = await supabase.from('tenants').select('*').eq('id', id).maybeSingle();
      if (tenantData) {
        setProfile({ ...tenantData, type: 'tenant' });
        return;
      }
      setProfile({ type: 'tenant' });
    }
    loadProfile();
  }, [id]);

  // If editing an existing review, pre-fill the form from the DB
  useEffect(() => {
    if (!editingReviewId) return;
    async function loadExisting() {
      const { data: rev } = await supabase.from('reviews').select('*').eq('id', editingReviewId).maybeSingle();
      if (!rev) return;
      setRating(rev.rating || 0);
      setText(rev.text || '');
      try {
        const v = typeof rev.votes === 'string' ? JSON.parse(rev.votes) : (rev.votes || {});
        setVotes(v);
      } catch(e) {}
    }
    loadExisting();
  }, [editingReviewId]);

  if (!user) {
    return (
      <div className="page-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 16 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0F2C4E', marginBottom: 8 }}>Sign in to write a review</h2>
          <p style={{ fontSize: 14, color: '#6C757D', marginBottom: 24 }}>You need an account to post a review. It's free and takes 30 seconds.</p>
          <button onClick={() => navigate('/signup?redirect=' + encodeURIComponent('/review/' + id))} className="btn btn-primary btn-lg" style={{ width: '100%', marginBottom: 10 }}>
            Sign Up Free
          </button>
          <button onClick={() => navigate('/login?redirect=' + encodeURIComponent('/review/' + id))} className="btn btn-outline btn-lg" style={{ width: '100%' }}>
            Log In
          </button>
        </div>
      </div>
    );
  }

  const isManager = profile?.type === 'manager';

  // A user may only review the OTHER side. A tenant reviews property
  // management; property management reviews tenants. Block same-side
  // reviews (e.g. a tenant trying to review another tenant) — this is
  // the gap that let a tenant review a tenant right after signing up.
  const sameSideConflict = profile && (
    (profile.type === 'tenant'  && user.role === 'tenant') ||
    (profile.type === 'manager' && user.role === 'manager')
  );
  if (sameSideConflict) {
    const wantType = user.role === 'tenant' ? 'manager' : 'tenant';
    const wantLabel = user.role === 'tenant' ? 'property management' : 'a tenant';
    return (
      <div className="page-wrap" style={{ background: '#F5EFE3', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 16 }}>
        <div style={{ textAlign: 'center', maxWidth: 420, background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, padding: 28, boxShadow: '0 4px 12px rgba(120,90,30,0.10)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚫</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400, color: '#0F2C4E', marginBottom: 8 }}>
            You can't review {profile.type === 'tenant' ? 'another tenant' : 'another property manager'}
          </h2>
          <p style={{ fontSize: 14, color: '#6B6358', lineHeight: 1.6, marginBottom: 20 }}>
            You're signed in as a {user.role === 'tenant' ? 'renter / tenant' : 'property management company'}.
            You can only review {wantLabel}.
          </p>
          <button
            onClick={() => navigate('/find?type=' + wantType)}
            style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 50, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Review {wantLabel} instead
          </button>
        </div>
      </div>
    );
  }

  const categories = isManager ? MANAGER_CATEGORIES : TENANT_CATEGORIES;
  // All categories EXCEPT the final rent_again get star ratings; rent_again
  // stays as a thumbs-up/down decision since it's a binary choice.
  const starCategories = categories.filter(c => c.id !== 'rent_again');
  const rentAgainCategory = categories.filter(c => c.id === 'rent_again');

  // Validate inputs and open the disclaimer modal. The actual DB insert runs
  // only AFTER the user acknowledges the disclaimer (see runSubmit below).
  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingNow = new Set();
    starCategories.forEach(c => {
      const v = votes[c.id];
      if (typeof v !== 'number' || v < 1 || v > 5) missingNow.add(c.id);
    });
    rentAgainCategory.forEach(c => {
      const v = votes[c.id];
      if (v !== 'up' && v !== 'down') missingNow.add(c.id);
    });
    setMissing(missingNow);
    const overallMissing = !rating;
    setRatingError(overallMissing);
    if (missingNow.size > 0 || overallMissing) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (text.trim().length > 0 && text.trim().length < 20) {
      alert('Please write at least 20 characters in your review.');
      return;
    }
    setShowDisclaimer(true);
  };

  const clearMissing = (id) => {
    setMissing(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleVotesChange = (next) => {
    setVotes(next);
    Object.keys(next).forEach(k => {
      const v = next[k];
      if (typeof v === 'number' && v >= 1 && v <= 5) clearMissing(k);
      if (v === 'up' || v === 'down') clearMissing(k);
    });
  };

  // Fired by DisclaimerModal after the user scrolls + checks + clicks accept.
  const runSubmit = async () => {
    setShowDisclaimer(false);
    setAiChecking(true);
    setAiViolations([]);
    const aiResult = { approved: true, violations: [], suggestion: '' };
    setAiChecking(false);

    if (!aiResult.approved) {
      setAiViolations(aiResult.violations || []);
      setAiSuggestion(aiResult.suggestion || '');
      return;
    }

    setSaving(true);
    const today = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    let error;
    if (isEditing) {
      // UPDATE existing review — keep the original date, swap rating/text/votes
      const res = await supabase.from('reviews').update({
        rating,
        text,
        votes: JSON.stringify(votes),
      }).eq('id', editingReviewId);
      error = res.error;
    } else {
      const res = await supabase.from('reviews').insert({
        profile_id: id,
        profile_type: profile?.type || 'manager',
        author: user.name,
        rating,
        text,
        date: today,
        votes: JSON.stringify(votes),
        vote_comments: '{}',
      });
      error = res.error;
    }

    if (!error) {
      const { data: existing } = await supabase.from('reviews').select('rating').eq('profile_id', id);
      if (existing) {
        const avg = existing.reduce((sum, r) => sum + r.rating, 0) / existing.length;
        await supabase.from('managers').update({
          rating: Math.round(avg * 10) / 10,
          review_count: existing.length,
        }).eq('id', id);
      }
      setSubmitted(true);
    } else {
      alert('Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  if (submitted) {
    return (
      <div style={{
        background: '#F5EFE3',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 20px',
      }}>
        <div style={{
          background: '#FAF6EC',
          border: '1px solid rgba(184,137,58,0.22)',
          borderRadius: 16,
          boxShadow: '0 4px 16px rgba(120,90,30,0.10)',
          padding: '36px 28px',
          textAlign: 'center',
          maxWidth: 440, width: '100%',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#E8F5EE', border: '2px solid #1B7A4B',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 30, color: '#1B7A4B', fontWeight: 700,
          }}>✓</div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400, color: '#0F2C4E', marginBottom: 8, lineHeight: 1.25 }}>
            {isEditing ? 'Changes saved' : 'Review submitted'}
          </h1>
          <p style={{ fontSize: 14, color: '#6B6358', marginBottom: 24, lineHeight: 1.6 }}>
            {isEditing ? 'Your updated review is live on the profile.' : 'Thank you. Your review is now live on the profile.'}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/profile/' + id)}
              style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
              View profile
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ background: 'transparent', color: '#0F2C4E', border: '1.5px solid #0F2C4E', padding: '10px 20px', borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 16 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer', marginBottom: 12, padding: '6px 0' }}>
          ← Back
        </button>

        <form onSubmit={handleSubmit}>

          {/* Profile header + Overall rating — left/right card */}
          <div style={{
            background: '#FAF6EC',
            borderRadius: 16,
            padding: 'clamp(16px, 4vw, 22px)',
            marginBottom: 20,
            border: '1px solid rgba(184,137,58,0.18)',
            boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
          }}>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
              {isEditing ? 'Edit your review' : 'Write a review'}
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(20px, 4.5vw, 26px)', color: '#0F2C4E', fontWeight: 400, marginBottom: 8, lineHeight: 1.2 }}>
                {profile?.name || 'Loading…'}
              </h1>
              {profile?.address && <div style={{ fontSize: 13, color: '#6B6358', marginBottom: 3, lineHeight: 1.5 }}>📍 {profile.address}</div>}
              {profile?.phone && <div style={{ fontSize: 13, color: '#6B6358', marginBottom: 3, lineHeight: 1.5 }}>📞 {profile.phone}</div>}
              {profile?.neighborhood && !profile?.address && <div style={{ fontSize: 13, color: '#6B6358', lineHeight: 1.5 }}>📍 {profile.neighborhood}</div>}
            </div>
          </div>

          {/* Per-category star ratings (everything except rent_again) */}
          <div className="form-group">
            <label style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>Rate each category</label>
            <p style={{ fontSize: 12, color: '#6C757D', marginBottom: 10 }}>1 star = poor · 5 stars = excellent</p>
            <StarVoting
              categories={starCategories}
              values={votes}
              onChange={handleVotesChange}
              missing={missing}
            />
          </div>

          {/* Overall rating — sits right below the per-category list */}
          <div className="form-group" style={{
            background: '#FAF6EC',
            borderRadius: 12,
            padding: '14px 16px',
            border: `1.5px solid ${ratingError ? '#C0392B' : 'rgba(184,137,58,0.22)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>
                Overall rating <span style={{ color: '#C0392B' }}>*</span>
              </div>
              <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2, minHeight: 14 }}>
                {rating === 0 && 'Tap a star to set an overall rating'}
                {rating === 1 && '⭐ Poor'}
                {rating === 2 && '⭐⭐ Below average'}
                {rating === 3 && '⭐⭐⭐ Average'}
                {rating === 4 && '⭐⭐⭐⭐ Good'}
                {rating === 5 && '⭐⭐⭐⭐⭐ Excellent'}
              </div>
            </div>
            <StarPicker value={rating} onChange={(val) => { setRating(val); setRatingError(false); }} />
          </div>
          {ratingError && (
            <div style={{ marginTop: -8, marginBottom: 16, fontSize: 12, color: '#C0392B' }}>
              ⚠️ Please select an overall rating before submitting.
            </div>
          )}

          {/* Final yes/no — would you rent again? */}
          <div className="form-group">
            <label style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>One last question</label>
            <p style={{ fontSize: 12, color: '#6C757D', marginBottom: 10 }}>{isManager ? 'Would you rent from this property management again?' : 'Would you rent to this tenant again?'}</p>
            <ThumbsVoting
              categories={rentAgainCategory}
              values={votes}
              onChange={handleVotesChange}
              missing={missing}
            />
          </div>

          {/* Written review */}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>Written review</label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Describe your experience (optional). Be honest and specific — your review helps others make better decisions."
              rows={5}
            />
            <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 4 }}>{text.length} characters (optional, min 20 if provided)</div>
          </div>

          {/* Lease verification */}
          <div className="form-group">
            <label style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>Lease verification (optional)</label>
            <div style={{ border: '1.5px dashed #E9ECEF', borderRadius: 10, padding: '20px', textAlign: 'center', background: '#F8F9FA' }}>
              <div style={{ fontSize: 13, color: '#6C757D' }}>Upload a photo or PDF of your lease</div>
              <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 4 }}>Not shown publicly. Prevents fake reviews.</div>
              <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 12 }}>Choose file</button>
            </div>
          </div>

          {/* Review guidelines */}
          

          {/* Fair Housing notice */}
          <div style={{ background: '#FAF6EC', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 12, color: '#6B6358', lineHeight: 1.6, border: '1px solid rgba(184,137,58,0.22)' }}>
            <strong style={{ color: '#0F2C4E' }}>Fair Housing Notice:</strong> Reviews must not reference race, religion, national origin, sex, disability, familial status, or other protected classes. Violations will be removed.
          </div>

          {/* AI violation warning */}
          {aiViolations.length > 0 && (
            <div style={{ background: '#FDEDEB', border: '1.5px solid #C0392B', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#C0392B', marginBottom: 8 }}>
                ⚠️ Your review cannot be submitted — please fix the following:
              </div>
              {aiViolations.map((v, i) => (
                <div key={i} style={{ fontSize: 13, color: '#343A40', marginBottom: 4, display: 'flex', gap: 6 }}>
                  <span style={{ color: '#C0392B' }}>✗</span> {v}
                </div>
              ))}
              {aiSuggestion && (
                <div style={{ fontSize: 12, color: '#6C757D', marginTop: 8, padding: '8px', background: 'rgba(0,0,0,0.04)', borderRadius: 6 }}>
                  💡 {aiSuggestion}
                </div>
              )}
            </div>
          )}

          {/* Submit button — navy blue, no charge */}
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            style={{ width: '100%', opacity: saving ? 0.7 : 1, background: '#0F2C4E' }}
            disabled={saving || aiChecking}
          >
            {aiChecking ? 'Checking review...' : saving ? (isEditing ? 'Saving changes...' : 'Submitting...') : (isEditing ? 'Save changes — Free' : 'Submit Review — Free')}
          </button>

          <p style={{ fontSize: 12, color: '#6B6358', textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
            Questions marked with a red <span style={{ color: '#C0392B', fontWeight: 700 }}>*</span> are required to submit your review.
          </p>
          <p style={{ fontSize: 11, color: '#ADB5BD', textAlign: 'center', marginTop: 6, lineHeight: 1.6 }}>
            Reviews are free to post. Viewing full profiles requires a subscription or pay-per-search.
          </p>
        </form>
      </div>

      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={runSubmit}
        actionLabel={isEditing ? 'Save changes' : 'Submit my review'}
      />
    </div>
  );
}
